import { CellStyle } from '@shared/documents/functions/gridSizing';
import { Editor, Plugin } from 'grapesjs';
import { mustacheFunction } from './functions';

const TABLE_WIDTH = 580; // page width - margins

const generateGridSizingCall = (component: any, editor: Editor) => {
  const dataVariable = component.get('dataVariable') || 'products';
  const tableWidth = component.get('tableWidth') ?? TABLE_WIDTH;
  const rowHeightScalar = component.get('rowHeightScalar') ?? 1.5;
  const headerFooterHeightScalar = component.get('headerFooterHeightScalar') ?? 1.5;
  const safetyPadX = component.get('safetyPadX') ?? 1;

  const columns = component.components();
  const headers: string[] = [];
  const rowTemplates: string[] = [];
  const footers: string[] = [];

  columns.each((column: any) => {
    const cells = column.components();
    const headerCell = cells.at(0);
    const dataCell = cells.at(1);
    const footerCell = cells.at(2);

    if (headerCell) {
      // Extract text content from nested components
      const headerContent =
        headerCell.components().length > 0
          ? headerCell.components().at(0)?.get('content') || ''
          : headerCell.get('content') || '';
      headers.push(headerContent);
    }

    if (dataCell) {
      const variable = dataCell.get('variable') || '';
      const prefix = dataCell.get('prefix') || '';
      const suffix = dataCell.get('suffix') || '';

      if (variable) {
        rowTemplates.push(`${prefix}{{{${variable}}}}${suffix}`);
      } else {
        const content = dataCell.get('content') || '';
        rowTemplates.push(content);
      }
    }

    if (footerCell) {
      // Extract text content from nested components
      const footerContent =
        footerCell.components().length > 0
          ? footerCell.components().at(0)?.get('content') || ''
          : footerCell.get('content') || '';
      footers.push(footerContent);
    }
  });

  const cssManager = editor.Css;
  const headerStyles: Partial<CellStyle>[] = [];
  const rowStyles: Partial<CellStyle>[] = [];
  const footerStyles: Partial<CellStyle>[] = [];

  // Get table-level styles for fallback - check actual table component first
  let tableFontSize: number | undefined;

  // First try to get font size from the table component's own CSS rules
  const tableId = component.ccid || component.cid || '';
  const tableClasses = component.get('classes')?.pluck('name') || [];

  // Check table's ID rule first
  if (tableId) {
    const idRule = cssManager.getRule(`#${tableId}`);
    if (idRule) {
      const idStyles = idRule.getStyle();
      if (idStyles['font-size']) {
        tableFontSize = parseInt(idStyles['font-size']?.toString());
      }
    }
  }

  // Then check table's class rules if no font size found yet
  if (!tableFontSize) {
    for (const className of tableClasses) {
      const classRule = cssManager.getRule(`.${className}`);
      if (classRule) {
        const classStyles = classRule.getStyle();
        if (classStyles['font-size']) {
          tableFontSize = parseInt(classStyles['font-size']?.toString());
          break;
        }
      }
    }
  }

  // Finally fall back to default variable-column-table rule
  if (!tableFontSize) {
    const defaultTableRule = cssManager.getRule('.variable-column-table');
    if (defaultTableRule) {
      const tableStyles = defaultTableRule.getStyle();
      if (tableStyles['font-size']) {
        tableFontSize = parseInt(tableStyles['font-size']?.toString());
      }
    }
  }

  const extractStylesFromRules = (rules: any[]): Partial<CellStyle> => {
    const cellStyle: Partial<CellStyle> = {};

    rules.forEach((rule) => {
      if (rule) {
        const styles = rule.getStyle();

        if (styles.padding) {
          const paddingValue = parseInt(styles.padding?.toString());
          if (paddingValue) {
            cellStyle.padX = paddingValue;
            cellStyle.padY = paddingValue;
          }
        }

        if (styles['font-size']) {
          const fontSizeValue = parseInt(styles['font-size']?.toString());
          if (fontSizeValue) {
            cellStyle.fontSize = fontSizeValue;
          }
        }

        if (styles['font-weight']) {
          const fontWeightValue = parseInt(styles['font-weight']?.toString());
          if (fontWeightValue) {
            cellStyle.fontWeight = fontWeightValue;
          }
        }
      }
    });

    return cellStyle;
  };

  columns.each((column: any) => {
    const cells = column.components();
    const headerCell = cells.at(0);
    const dataCell = cells.at(1);
    const footerCell = cells.at(2);

    // Extract header cell styles from classes and ID
    const headerCellStyle: Partial<CellStyle> = {};
    if (headerCell) {
      const headerId = headerCell.ccid || headerCell.cid || '';
      const headerClasses = headerCell.get('classes')?.pluck('name') || [];

      const rules = [];

      // Get rules from classes
      headerClasses.forEach((className: string) => {
        const rule = cssManager.getRule(`.${className}`);
        if (rule) rules.push(rule);
      });

      // Get rule from ID
      if (headerId) {
        const idRule = cssManager.getRule(`#${headerId}`);
        if (idRule) rules.push(idRule);
      }

      Object.assign(headerCellStyle, extractStylesFromRules(rules));

      if (!headerCellStyle.fontSize && tableFontSize) {
        headerCellStyle.fontSize = tableFontSize;
      }
    }
    headerStyles.push(headerCellStyle);

    // Extract data cell styles from classes and ID
    const dataCellStyle: Partial<CellStyle> = {};
    if (dataCell) {
      const dataId = dataCell.ccid || dataCell.cid || '';
      const dataCellClasses = dataCell.get('classes')?.pluck('name') || [];

      const rules = [];

      // Get rules from classes
      dataCellClasses.forEach((className: string) => {
        const rule = cssManager.getRule(`.${className}`);

        if (rule) rules.push(rule);
      });

      // Get rule from ID
      if (dataId) {
        const idRule = cssManager.getRule(`#${dataId}`);

        if (idRule) rules.push(idRule);
      }

      Object.assign(dataCellStyle, extractStylesFromRules(rules));

      if (!dataCellStyle.fontSize && tableFontSize) {
        dataCellStyle.fontSize = tableFontSize;
      }
    }
    rowStyles.push(dataCellStyle);

    // Extract footer cell styles from classes and ID
    const footerCellStyle: Partial<CellStyle> = {};
    if (footerCell) {
      const footerId = footerCell.ccid || footerCell.cid || '';
      const footerClasses = footerCell.get('classes')?.pluck('name') || [];

      const rules = [];

      // Get rules from classes
      footerClasses.forEach((className: string) => {
        const rule = cssManager.getRule(`.${className}`);
        if (rule) rules.push(rule);
      });

      // Get rule from ID
      if (footerId) {
        const idRule = cssManager.getRule(`#${footerId}`);
        if (idRule) rules.push(idRule);
      }

      Object.assign(footerCellStyle, extractStylesFromRules(rules));

      if (!footerCellStyle.fontSize && tableFontSize) {
        footerCellStyle.fontSize = tableFontSize;
      }
    }
    footerStyles.push(footerCellStyle);
  });

  return mustacheFunction('measureTable', {
    dataSource: `${dataVariable}.items`,
    header: headers,
    row: rowTemplates,
    footer: footers,
    tableWidth: tableWidth,
    gridId: component.ccid || component.cid || '',
    rowHeightScalar: rowHeightScalar,
    headerFooterHeightScalar: headerFooterHeightScalar,
    safetyPadX: safetyPadX,
    cellStyles: { header: headerStyles, row: rowStyles, footer: footerStyles },
  });
};

const plugin: Plugin<{}> = (editor: Editor) => {
  const tableStyles = `
    .variable-column-table {
      display: flex;
      flex-direction: row;
      width: 100%;
      border: 1px solid #E0E0E0;
      border-radius: 4px;
      font-size: 9px;
    }

    .variable-table-column {
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .variable-column-cell {
      padding: 6px 10px;
      border-bottom: 1px solid #E0E0E0;
    }

    .variable-table-column .variable-column-cell:first-child {
      border-top: none;
    }

    .variable-table-column .variable-column-cell:last-child {
      border-bottom: none;
    }

    .variable-column-header {
      background: #F7F8F3;
      font-weight: 500;
    }

    .variable-column-footer {
      font-weight: 500;
    }

    .variable-column-nested {
      border-top: 1px dashed #E0E0E0;
      font-style: italic;
      opacity: 0.8;
    }
  `;

  const privateCls = ['.variable-column-table', '.variable-table-column', '.variable-column-cell'];

  editor.on(
    'selector:add',
    (selector) => privateCls.indexOf(selector.getFullName()) >= 0 && selector.set('private', 1)
  );

  // Header cell component with editable text content
  editor.DomComponents.addType('variable-column-header-cell', {
    model: {
      defaults: {
        tagName: 'div',
        attributes: { class: 'variable-column-cell variable-column-header' },
        draggable: true,
        droppable: true,
        editable: true,
        components: [{ content: 'Header Text' }],
      },
      toHTML: function () {
        const classes = this.get('classes').pluck('name').join(' ');
        const parentColumn = this.parent();
        const parentTable = parentColumn?.parent();
        const gridId = parentTable?.ccid || parentTable?.cid || '';
        const gridPrefix = gridId ? `grid-${gridId}` : 'grid';
        let columnIndex = 0;

        if (parentTable?.components) {
          parentTable.components().each((col: any, index: number) => {
            if (col === parentColumn) columnIndex = index;
          });
        }

        const content =
          this.components().length > 0
            ? this.components()
                .map((comp) => comp.toHTML())
                .join('')
            : '';

        return `<div id="${this.ccid}" class="${classes} ${gridPrefix}-col-${columnIndex} ${gridPrefix}-header">${content}</div>`;
      },
    },
  });

  // Footer cell component with editable text content
  editor.DomComponents.addType('variable-column-footer-cell', {
    model: {
      defaults: {
        tagName: 'div',
        attributes: { class: 'variable-column-cell variable-column-footer' },
        draggable: true,
        droppable: true,
        editable: true,
        components: [{ content: 'Footer Text' }],
      },
      toHTML: function () {
        const classes = this.get('classes').pluck('name').join(' ');
        const parentColumn = this.parent();
        const parentTable = parentColumn?.parent();
        const gridId = parentTable?.ccid || parentTable?.cid || '';
        const gridPrefix = gridId ? `grid-${gridId}` : 'grid';
        let columnIndex = 0;

        if (parentTable?.components) {
          parentTable.components().each((col: any, index: number) => {
            if (col === parentColumn) columnIndex = index;
          });
        }

        const content =
          this.components().length > 0
            ? this.components()
                .map((comp) => comp.toHTML())
                .join('')
            : '';

        return `<div id="${this.ccid}" class="${classes} ${gridPrefix}-col-${columnIndex} ${gridPrefix}-footer">${content}</div>`;
      },
    },
  });

  // Variable cell component that supports mustache templating
  editor.DomComponents.addType('variable-column-cell', {
    model: {
      defaults: {
        tagName: 'div',
        attributes: { class: 'variable-column-cell' },
        draggable: true,
        droppable: true,
        editable: true,
        traits: [
          { type: 'text', label: 'Variable', name: 'variable', changeProp: true },
          { type: 'text', label: 'Prefix', name: 'prefix', changeProp: true },
          { type: 'text', label: 'Suffix', name: 'suffix', changeProp: true },
        ],
      },
      init() {
        this.listenTo(this, 'change:variable', this.handleContentChange);
        this.listenTo(this, 'change:prefix', this.handleContentChange);
        this.listenTo(this, 'change:suffix', this.handleContentChange);
      },
      handleContentChange() {
        this.view.render();
      },
      toHTML: function () {
        const variable = this.get('variable') || '';
        const prefix = this.get('prefix') || '';
        const suffix = this.get('suffix') || '';
        const classes = this.get('classes').pluck('name').join(' ');
        const isNested = this.get('nested');

        // Get column index from parent column
        const parentColumn = this.parent();
        const parentTable = parentColumn?.parent();
        const dataVariable = parentTable?.get('dataVariable') || 'products';
        const nestedVariable = parentTable?.get('nestedVariable') || '';
        const gridId = parentTable?.ccid || parentTable?.cid || '';
        const gridPrefix = gridId ? `grid-${gridId}` : 'grid';
        let columnIndex = 0;
        let rowIndex = 0;

        if (parentTable?.components) {
          parentTable.components().each((col: any, index: number) => {
            if (col === parentColumn) columnIndex = index;
          });
        }

        // Get row index within the column for nested cells
        if (parentColumn?.components) {
          const rowCells = parentColumn.components().filter((cell) => cell.get('type') === 'variable-column-cell');
          rowIndex = rowCells.indexOf(this);
        }

        // Check if this is a header cell (first child of column)
        const isHeader = parentColumn?.components().at(0) === this;

        if (isNested) {
          // Nested cell - don't render if no nested variable is set
          if (!nestedVariable || !variable) return '';

          // Nested row rendering with dashed border
          return `
{{#${dataVariable}.items}}
  {{#${nestedVariable}}}
    <div id="${this.ccid}-{{@../index}}-{{@index}}" class="${classes} ${gridPrefix}-col-${columnIndex} ${gridPrefix}-row-{{@../index}}-${rowIndex}" style="border-top: 1px dashed #E0E0E0;">${prefix}{{{${variable}}}}${suffix}</div>
  {{/${nestedVariable}}}
{{/${dataVariable}.items}}
          `.trim();
        } else if (variable && !isHeader) {
          // Main data cell with mustache iteration
          return `
{{#${dataVariable}.items}}
  <div id="${this.ccid}-{{index}}" class="${classes} ${gridPrefix}-col-${columnIndex} ${gridPrefix}-row-{{index}}">${prefix}{{{${variable}}}}${suffix}</div>
{{/${dataVariable}.items}}
          `.trim();
        } else {
          // Header cell or static content
          const content =
            this.get('content') || this.components().length > 0
              ? this.components()
                  .map((comp) => comp.toHTML())
                  .join('')
              : '';
          const headerClass = isHeader ? ` ${gridPrefix}-header` : '';
          return `<div id="${this.ccid}" class="${classes} ${gridPrefix}-col-${columnIndex}${headerClass}">${content}</div>`;
        }
      },
    },
    view: {
      onRender: function () {
        const variable = this.model.get('variable');
        const prefix = this.model.get('prefix') || '';
        const suffix = this.model.get('suffix') || '';
        const classes = this.model.get('classes').pluck('name').join(' ');
        const isNested = this.model.get('nested');

        const nestedIndicator = isNested ? '<span style="font-style: italic; opacity: 0.6;">[nested] </span>' : '';

        if (variable) {
          this.el.innerHTML = `<div id="${this.ccid}" style="flex: 1 1 auto" class="${classes}">${nestedIndicator}${prefix}{{{${variable}}}}${suffix}</div>`;
        } else {
          // Show placeholder or static content
          const placeholder = isNested ? 'Set nested variable' : 'Click to edit or set variable';
          const content = this.model.get('content') || placeholder;
          this.el.innerHTML = `<div id="${this.ccid}" style="flex: 1 1 auto" class="${classes}">${nestedIndicator}${content}</div>`;
        }
      },
    },
  });

  // Column component
  editor.DomComponents.addType('variable-table-column', {
    model: {
      defaults: { tagName: 'div', attributes: { class: 'variable-table-column' }, draggable: true, droppable: true },
      toHTML: function () {
        const classes = this.get('classes').pluck('name').join(' ');

        // Find column index for grid class
        const parentTable = this.parent();
        const gridId = parentTable?.ccid || parentTable?.cid || '';
        const gridPrefix = gridId ? `grid-${gridId}` : 'grid';
        const parentColumns = parentTable?.components();
        let columnIndex = 0;
        if (parentColumns) {
          parentColumns.each((col: any, index: number) => {
            if (col === this) columnIndex = index;
          });
        }

        let childrenHtml = '';
        this.components().each((model) => {
          childrenHtml += model.toHTML();
        });

        return `<div class="${classes} wrap ${gridPrefix}-col-${columnIndex}">${childrenHtml}</div>`;
      },
    },
  });

  // Main table component with mustache templating
  editor.DomComponents.addType('variable-column-table', {
    model: {
      defaults: {
        tagName: 'div',
        attributes: { class: 'variable-column-table' },
        components: [
          {
            type: 'variable-table-column',
            attributes: { class: 'variable-table-column' },
            components: [
              {
                type: 'variable-column-header-cell',
                attributes: { class: 'variable-column-cell variable-column-header' },
                components: [{ content: 'Column 1', type: 'text' }],
              },
              {
                type: 'variable-column-cell',
                attributes: { class: 'variable-column-cell' },
                variable: 'column1',
                prefix: '',
                suffix: '',
              },
              {
                type: 'variable-column-footer-cell',
                attributes: { class: 'variable-column-cell variable-column-footer' },
                components: [{ content: 'Total' }],
              },
            ],
          },
          {
            type: 'variable-table-column',
            attributes: { class: 'variable-table-column' },
            components: [
              {
                type: 'variable-column-header-cell',
                attributes: { class: 'variable-column-cell variable-column-header' },
                components: [{ content: 'Column 2', type: 'text' }],
              },
              {
                type: 'variable-column-cell',
                attributes: { class: 'variable-column-cell' },
                variable: 'column2',
                prefix: '',
                suffix: '',
              },
              {
                type: 'variable-column-footer-cell',
                attributes: { class: 'variable-column-cell variable-column-footer' },
                components: [{ content: '' }],
              },
            ],
          },
          {
            type: 'variable-table-column',
            attributes: { class: 'variable-table-column' },
            components: [
              {
                type: 'variable-column-header-cell',
                attributes: { class: 'variable-column-cell variable-column-header' },
                components: [{ content: 'Column 3', type: 'text' }],
              },
              {
                type: 'variable-column-cell',
                attributes: { class: 'variable-column-cell' },
                variable: 'column3',
                prefix: '',
                suffix: '',
              },
              {
                type: 'variable-column-footer-cell',
                attributes: { class: 'variable-column-cell variable-column-footer' },
                components: [{ content: '' }],
              },
            ],
          },
        ],
        styles: tableStyles,
        traits: [
          { label: 'Data Variable', type: 'text', name: 'dataVariable', default: 'products', changeProp: true },
          { label: 'Table Width (px)', type: 'number', name: 'tableWidth', default: 580, changeProp: true },
          {
            label: 'Row Height Scalar',
            type: 'number',
            name: 'rowHeightScalar',
            default: 1.5,
            min: 0.5,
            max: 3.0,
            step: 0.1,
            changeProp: true,
          },
          {
            label: 'Header/Footer Height Scalar',
            type: 'number',
            name: 'headerFooterHeightScalar',
            default: 1.5,
            min: 0.5,
            max: 3.0,
            step: 0.1,
            changeProp: true,
          },
          {
            label: 'Safety Pad X (px)',
            type: 'number',
            name: 'safetyPadX',
            default: 1,
            min: 0,
            max: 20,
            step: 1,
            changeProp: true,
          },
          { label: 'Nested Variable', type: 'text', name: 'nestedVariable', default: '', changeProp: true },
        ],
      },
      init() {
        this.listenTo(this, 'change:dataVariable', this.handleDataVariableChange);
        this.listenTo(this, 'change:nestedVariable', this.handleNestedVariableChange);
      },
      handleDataVariableChange() {
        this.view.render();
      },
      handleNestedVariableChange() {
        const nestedVariable = this.get('nestedVariable');
        const hasNested = !!nestedVariable;

        if (hasNested) {
          // Add nested row components to each column
          const columns = this.components().filter((comp) => comp.get('type') === 'variable-table-column');

          columns.forEach((column) => {
            const cells = column.components();
            const mainRowCell = cells.find(
              (cell) => cell.get('type') === 'variable-column-cell' && !cell.get('nested')
            );
            const nestedRowCell = cells.find(
              (cell) => cell.get('type') === 'variable-column-cell' && cell.get('nested')
            );

            if (mainRowCell && !nestedRowCell) {
              const mainRowIndex = cells.indexOf(mainRowCell);
              column.append(
                {
                  type: 'variable-column-cell',
                  attributes: { class: 'variable-column-cell variable-column-nested' },
                  nested: true,
                  variable: '',
                  prefix: '',
                  suffix: '',
                },
                { at: mainRowIndex + 1 }
              );
            }
          });
        } else {
          // Remove nested row components from each column
          const columns = this.components().filter((comp) => comp.get('type') === 'variable-table-column');

          columns.forEach((column) => {
            const nestedRowCell = column
              .components()
              .find((cell) => cell.get('type') === 'variable-column-cell' && cell.get('nested'));
            if (nestedRowCell) {
              nestedRowCell.remove();
            }
          });
        }

        this.view.render();
      },
      toHTML: function () {
        const dataVariable = this.get('dataVariable') || 'products';
        const gridSizingCall = generateGridSizingCall(this, editor);
        const classes = this.get('classes').pluck('name').join(' ');

        let childrenHtml = '';
        this.components().each((model) => {
          childrenHtml += model.toHTML();
        });

        return `
          {{^${dataVariable}.isEmpty}}
            ${gridSizingCall}
            <div id="${this.ccid}" class="${classes} wrap">
              ${childrenHtml}
            </div>
          {{/${dataVariable}.isEmpty}}
        `.trim();
      },
    },
  });

  editor.BlockManager.add('variable-column-table-block', {
    label: 'Table V2',
    content: { type: 'variable-column-table' },
    category: 'Tables',
    attributes: { class: 'gjs-fonts gjs-f-text' },
  });
};

export default plugin;
